// ============================================================
// Modal — centered dialog with backdrop. Portals to document.body
// when available; in SSR contexts the portal is skipped and the
// dialog renders inline (smoke tests rely on this).
// ============================================================

import { useEffect } from 'react'
import { createPortal } from 'react-dom'

function ModalBody({ open, onClose, title, children, width = 480, footer }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(6,6,10,0.72)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        animation: 'olv-fade-in 200ms var(--ease-standard)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          background: 'var(--olv-elevated)',
          border: '1px solid var(--olv-panel-border)',
          borderRadius: 'var(--olv-radius-panel)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {title && (
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--olv-panel-border)',
              fontSize: 11,
              color: 'var(--olv-text-tertiary)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
            }}
          >
            {title}
          </div>
        )}
        <div style={{ padding: 18, overflowY: 'auto', flex: 1, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--olv-text-primary)' }}>
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--olv-panel-border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

export default function Modal(props) {
  if (typeof document === 'undefined') {
    // SSR — render inline so smoke tests don't crash on document.body access.
    return <ModalBody {...props} />
  }
  return createPortal(<ModalBody {...props} />, document.body)
}
